/**
 * ThemeCarousel v1.0.0
 * MIT License
 *
 * Lightweight carousel providing a Flickity-compatible API.
 * Supports slide and fade transitions, drag/touch, adaptive height,
 * cell selection, RTL, and contain mode.
 */
(() => {
  'use strict';

  const instances = new WeakMap();

  class ThemeCarousel {

    // ── Static ────────────────────────────────────────────────────────

    static data(elem) {
      if (typeof elem === 'string') elem = document.querySelector(elem);
      return elem ? instances.get(elem) : null;
    }

    // ── Constructor ───────────────────────────────────────────────────

    constructor(element, options) {
      if (typeof element === 'string') element = document.querySelector(element);
      if (!element) { console.error('ThemeCarousel: element not found'); return; }

      this.element = element;
      this.options = Object.assign({}, ThemeCarousel.defaults, options);

      this.selectedIndex = 0;
      this.selectedElement = null;
      this.selectedElements = [];
      this.selectedCells = [];
      this.selectedCell = null;
      this.selectedSlide = null;
      this.isActive = false;
      this.isDragging = false;
      this.isAnimating = false;
      this.isPointerDown = false;
      this.isDraggable = false;
      this.cells = [];
      this.slides = [];
      this.x = 0;

      this._listeners = {};
      this._cellData = [];
      this._totalWidth = 0;
      this._dragCleanup = null;
      this._resizeTimer = null;

      // Register event listeners from options.on before activation
      if (this.options.on) {
        Object.entries(this.options.on).forEach(([evt, fn]) => this.on(evt, fn));
      }

      this._create();
    }

    // ── Lifecycle ─────────────────────────────────────────────────────

    _create() {
      var rtl = this.options.rightToLeft;
      this.originSide = rtl ? 'right' : 'left';

      // Build DOM: element > viewport > slider > [children]
      this.viewport = document.createElement('div');
      this.viewport.className = 'slide-viewport';

      this.slider = document.createElement('div');
      this.slider.className = 'slide-wrapper';
      this.slider.style[this.originSide] = '0';

      while (this.element.firstChild) this.slider.appendChild(this.element.firstChild);
      this.viewport.appendChild(this.slider);
      this.element.appendChild(this.viewport);

      // Identify cells
      this._refreshCells();

      // State classes
      this.element.classList.add('slide-enabled');
      if (rtl) this.element.classList.add('flickity-rtl');
      if (this.options.fade) this.element.classList.add('is-fade');

      // Measure & position
      this._getSize();
      this._positionCells();

      // Resolve initial slide
      this._resolveInitialIndex();

      // Dragging
      this.updateDraggable();

      // Keyboard
      if (this.options.accessibility) {
        this.element.setAttribute('tabindex', '0');
        this._onKeydown = this._handleKeydown.bind(this);
        this.element.addEventListener('keydown', this._onKeydown);
      }

      // Apply initial selection (no animation)
      this._selectInternal(this.selectedIndex, true);

      // Resize
      if (this.options.resize) {
        this._onResize = () => {
          clearTimeout(this._resizeTimer);
          this._resizeTimer = setTimeout(() => {
            if (this.isDragging || this.isAnimating) return;
            this.resize();
          }, 150);
        };
        window.addEventListener('resize', this._onResize);
      }

      this.isActive = true;
      this.isInitActivated = true;
      instances.set(this.element, this);

      this._emit('ready');
    }

    destroy() {
      if (!this.isActive) return;

      if (this._dragCleanup) this._dragCleanup();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onKeydown) this.element.removeEventListener('keydown', this._onKeydown);
      clearTimeout(this._resizeTimer);

      this.element.classList.remove('slide-enabled', 'flickity-rtl', 'is-fade', 'is-draggable');
      this.element.removeAttribute('tabindex');

      // Move children back, strip inline styles
      while (this.slider.firstChild) {
        var child = this.slider.firstChild;
        child.style.position = '';
        child.style[this.originSide] = '';
        child.style.transform = '';
        child.style.opacity = '';
        child.classList.remove('is-selected');
        child.removeAttribute('aria-hidden');
        this.element.appendChild(child);
      }

      this.element.removeChild(this.viewport);
      this.isActive = false;
      this._listeners = {};
      instances.delete(this.element);
    }

    resize() {
      if (!this.isActive || this.isAnimating || this.isDragging) return;
      this._getSize();
      this._positionCells();
      this._positionSlider(false);
      if (this.options.adaptiveHeight) this._updateHeight();
      this._emit('resize');
      // Re-select current element to maintain alignment
      var sel = this.selectedElements && this.selectedElements[0];
      if (sel) this.selectCell(sel, false, true);
    }

    // ── Cell management ───────────────────────────────────────────────

    _refreshCells() {
      var children = Array.from(this.slider.children);
      this.cells = this.options.cellSelector
        ? children.filter(function(el) { return el.matches(this.options.cellSelector); }.bind(this))
        : children;
      // 1-to-1 slide mapping for compatibility
      this.slides = this.cells.map(function(cell, i) { return { cells: [cell], target: 0, index: i }; });
    }

    _resolveInitialIndex() {
      var init = this.options.initialIndex;
      if (typeof init === 'string') {
        var el = this.slider.querySelector(init);
        if (el) {
          var idx = this.cells.indexOf(el);
          if (idx >= 0) this.selectedIndex = idx;
        }
      } else if (typeof init === 'number' && init > 0) {
        this.selectedIndex = Math.min(init, this.cells.length - 1);
      }
    }

    reloadCells() {
      this._refreshCells();
      this._positionCells();
      if (this.options.adaptiveHeight) this._updateHeight();
    }

    // ── Measurement & positioning ─────────────────────────────────────

    _getSize() {
      this._viewportWidth = this.viewport.offsetWidth || this.element.offsetWidth;
      this._setCellAlign();
      this.cursorPosition = this._viewportWidth * this.cellAlign;
    }

    _setCellAlign() {
      var ca = this.options.cellAlign;
      var shorthands = {
        center: { left: 0.5, right: 0.5 },
        left:   { left: 0,   right: 1 },
        right:  { right: 0,  left: 1 }
      };
      var sh = shorthands[ca];
      this.cellAlign = sh ? sh[this.originSide] : (typeof ca === 'number' ? ca : 0.5);
    }

    _positionCells() {
      if (!this.cells.length) return;

      // ── Fade mode: cells are stacked by CSS ──
      if (this.options.fade) {
        this._cellData = this.cells.map(function(cell) {
          cell.setAttribute('aria-hidden', 'true');
          return { x: 0, width: cell.offsetWidth, beginMargin: 0, endMargin: 0, outerWidth: cell.offsetWidth, target: 0 };
        });
        this._totalWidth = 0;
        this.slides.forEach(function(s, i) { if (this._cellData[i]) s.target = 0; }.bind(this));
        return;
      }

      // ── Slide mode ──
      var sideOffset = this.originSide === 'left' ? 1 : -1;
      var usePercent = this.options.percentPosition;
      var vw = this._viewportWidth;
      var x = 0;
      var self = this;

      this._cellData = [];

      this.cells.forEach(function(cell) {
        cell.style.position = 'absolute';
        cell.setAttribute('aria-hidden', 'true');
        cell.style[self.originSide] = '0';

        var cs = getComputedStyle(cell);
        var ml = parseFloat(cs.marginLeft)  || 0;
        var mr = parseFloat(cs.marginRight) || 0;
        var w  = cell.offsetWidth;
        var bm = self.originSide === 'left' ? ml : mr;
        var em = self.originSide === 'left' ? mr : ml;
        var target = x + bm + w * self.cellAlign;

        self._cellData.push({ x: x, width: w, marginLeft: ml, marginRight: mr, outerWidth: ml + w + mr, beginMargin: bm, endMargin: em, target: target });

        // Render cell position via transform
        var ax = usePercent && w > 0 ? x * sideOffset * (vw / w) : x * sideOffset;
        cell.style.transform = usePercent && vw > 0
          ? 'translateX(' + (Math.round(ax / vw * 1e4) * 0.01) + '%)'
          : 'translateX(' + Math.round(ax) + 'px)';

        x += ml + w + mr;
      });

      this._totalWidth = x;

      // Sync slide targets
      this.slides.forEach(function(s, i) { if (self._cellData[i]) s.target = self._cellData[i].target; });

      // Contain
      this._containSlides();
    }

    _containSlides() {
      if (!this.options.contain || this.options.wrapAround || !this.cells.length) return;

      var vw    = this._viewportWidth;
      var first = this._cellData[0];
      var last  = this._cellData[this._cellData.length - 1];
      var contentWidth = this._totalWidth - last.endMargin;
      var small = contentWidth < vw;

      var beginBound = this.cursorPosition + first.beginMargin;
      var endBound   = contentWidth - vw * (1 - this.cellAlign);

      this.slides.forEach(function(slide) {
        if (small) {
          slide.target = contentWidth * this.cellAlign;
        } else {
          slide.target = Math.max(beginBound, Math.min(endBound, slide.target));
        }
      }.bind(this));
    }

    _positionSlider(animate) {
      if (this.options.fade) {
        this._setSliderTransform(0, false);
        this._updateFadeOpacity();
        return;
      }
      var slide = this.slides[this.selectedIndex];
      if (!slide) return;

      this.x = -slide.target;
      this._setSliderTransform(this.x + this.cursorPosition, animate);
    }

    _setSliderTransform(x, animate) {
      var px = Math.round(this.options.rightToLeft ? -x : x);
      var self = this;

      if (animate) {
        this.isAnimating = true;
        this.slider.style.transition = 'transform 0.35s ease';
        this.slider.style.transform = 'translate3d(' + px + 'px,0,0)';

        var fallback = setTimeout(onEnd, 500);

        function onEnd() {
          clearTimeout(fallback);
          self.slider.removeEventListener('transitionend', onEnd);
          self.isAnimating = false;
          self.slider.style.transition = '';
          self._emit('settle', [self.selectedIndex]);
        }
        this.slider.addEventListener('transitionend', onEnd);
      } else {
        this.isAnimating = false;
        this.slider.style.transition = '';
        this.slider.style.transform = 'translateX(' + px + 'px)';
      }
    }

    _updateFadeOpacity() {
      if (!this.options.fade) return;
      var idx = this.selectedIndex;
      this.cells.forEach(function(cell, i) { cell.style.opacity = i === idx ? '1' : '0'; });
    }

    _updateHeight() {
      if (!this.options.adaptiveHeight || !this.cells.length) return;
      var cell = this.cells[this.selectedIndex];
      if (!cell) return;
      this.viewport.style.transition = 'height 0.3s ease';
      this.viewport.style.height = cell.offsetHeight + 'px';
    }

    // ── Selection ─────────────────────────────────────────────────────

    _selectInternal(index, instant) {
      if (!this.cells.length) return;
      index = Math.max(0, Math.min(index, this.cells.length - 1));

      var prevIndex = this.selectedIndex;

      // Unselect all cells
      this.cells.forEach(function(cell) {
        cell.classList.remove('is-selected');
        cell.setAttribute('aria-hidden', 'true');
      });

      this.selectedIndex = index;
      var cell = this.cells[index];
      cell.classList.add('is-selected');
      cell.removeAttribute('aria-hidden');

      this.selectedElement  = cell;
      this.selectedElements = [cell];
      this.selectedCells    = [cell];
      this.selectedCell     = cell;
      this.selectedSlide    = this.slides[index] || null;

      this._positionSlider(!instant);

      if (this.options.adaptiveHeight) this._updateHeight();

      this._emit('select', [index]);
      if (prevIndex !== index) this._emit('change', [index]);
      this._emit('cellSelect');
    }

    // ── Public API ────────────────────────────────────────────────────

    select(index, isWrap, isInstant) {
      if (!this.isActive) return;
      index = parseInt(index, 10);
      var len = this.cells.length;
      if (!len) return;

      if (this.options.wrapAround || isWrap) {
        index = ((index % len) + len) % len;
      } else {
        index = Math.max(0, Math.min(index, len - 1));
      }

      this._selectInternal(index, isInstant);
    }

    next(isWrap, isInstant) {
      this.select(this.selectedIndex + 1, isWrap, isInstant);
    }

    previous(isWrap, isInstant) {
      this.select(this.selectedIndex - 1, isWrap, isInstant);
    }

    selectCell(value, isWrap, isInstant) {
      if (value == null) return;

      if (typeof value === 'number') {
        return this.select(value, isWrap, isInstant);
      }

      if (typeof value === 'string') {
        if (/^[.#\[]/.test(value)) {
          var el = this.slider.querySelector(value);
          if (el) { var i = this.cells.indexOf(el); if (i >= 0) return this.select(i, isWrap, isInstant); }
        }
        var n = parseInt(value, 10);
        if (!isNaN(n)) return this.select(n, isWrap, isInstant);
        return;
      }

      // DOM Element
      if (value instanceof Element) {
        var i2 = this.cells.indexOf(value);
        if (i2 >= 0) return this.select(i2, isWrap, isInstant);
      }
    }

    updateDraggable() {
      var d = this.options.draggable;
      var should = (d === true) || (d === '>1' && this.cells.length > 1);

      if (should && !this.isDraggable) {
        this.isDraggable = true;
        this.element.classList.add('is-draggable');
        this._bindDrag();
      } else if (!should && this.isDraggable) {
        this.isDraggable = false;
        this.element.classList.remove('is-draggable');
        if (this._dragCleanup) { this._dragCleanup(); this._dragCleanup = null; }
      } else if (should) {
        this.isDraggable = true;
        this.element.classList.add('is-draggable');
      }
    }

    getCell(elem) {
      return this.cells.indexOf(elem) >= 0 ? elem : null;
    }

    getCellElements() {
      return this.cells.slice();
    }

    // ── Drag / Touch ──────────────────────────────────────────────────

    _bindDrag() {
      if (this._dragCleanup) return;

      var self = this;
      var startX = 0, startY = 0, sliderStartPx = 0;
      var isDragMoving = false, pointerDown = false, isHorizontal = null;
      var threshold = this.options.dragThreshold || 3;

      function xy(e) {
        return e.touches && e.touches.length
          ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
          : { x: e.clientX, y: e.clientY };
      }

      function readSliderPx() {
        var m = getComputedStyle(self.slider).transform;
        if (m && m !== 'none') {
          var v = m.match(/matrix.*\((.+)\)/);
          if (v) return parseFloat(v[1].split(', ')[4]) || 0;
        }
        return 0;
      }

      function onDown(e) {
        if (e.button && e.button !== 0) return;
        var tag = e.target.nodeName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

        pointerDown = true;
        isDragMoving = false;
        isHorizontal = null;

        var pt = xy(e);
        startX = pt.x;
        startY = pt.y;
        sliderStartPx = readSliderPx();

        self.viewport.classList.add('is-pointer-down');
        self.isPointerDown = true;

        if (e.type === 'mousedown') e.preventDefault();
      }

      function onMove(e) {
        if (!pointerDown) return;

        var pt = xy(e);
        var dx = pt.x - startX;
        var dy = pt.y - startY;

        if (isHorizontal === null && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
          isHorizontal = Math.abs(dx) >= Math.abs(dy);
        }
        if (isHorizontal === false) return;

        if (!isDragMoving) {
          if (Math.abs(dx) < threshold) return;
          isDragMoving = true;
          self.isDragging = true;
        }

        if (self.options.fade) return;

        self.slider.style.transition = '';
        self.slider.style.transform = 'translateX(' + Math.round(sliderStartPx + dx) + 'px)';

        if (e.cancelable) e.preventDefault();
      }

      function onUp(e) {
        if (!pointerDown) return;
        pointerDown = false;
        self.viewport.classList.remove('is-pointer-down');
        self.isPointerDown = false;

        if (!isDragMoving) {
          self.isDragging = false;
          var pointer = { clientX: startX, clientY: startY };
          var sel = self.options.cellSelector || '.slide-wrapper > *';
          var cellEl = e.target ? e.target.closest(sel) : null;
          self._emit('staticClick', [e, pointer, cellEl]);
          return;
        }

        isDragMoving = false;

        if (self.options.fade) { self.isDragging = false; return; }

        var endX;
        if (e.changedTouches && e.changedTouches.length) {
          endX = e.changedTouches[0].clientX;
        } else {
          endX = e.clientX;
        }

        var totalDx = endX - startX;
        var effective = self.options.rightToLeft ? -totalDx : totalDx;

        if (Math.abs(effective) > 40) {
          effective > 0 ? self.previous() : self.next();
        } else {
          self._positionSlider(true);
        }

        setTimeout(function() { self.isDragging = false; }, 10);
      }

      function onNativeDrag(e) { e.preventDefault(); }
      function onClickCapture(e) { if (self.isDragging) { e.preventDefault(); e.stopPropagation(); } }

      this.viewport.addEventListener('mousedown',  onDown);
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup',   onUp);
      this.viewport.addEventListener('touchstart', onDown, { passive: true });
      window.addEventListener('touchmove',   onMove, { passive: false });
      window.addEventListener('touchend',    onUp);
      window.addEventListener('touchcancel', onUp);
      this.viewport.addEventListener('dragstart', onNativeDrag);
      this.viewport.addEventListener('click',     onClickCapture, true);

      this._dragCleanup = function() {
        self.viewport.removeEventListener('mousedown',  onDown);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup',   onUp);
        self.viewport.removeEventListener('touchstart', onDown);
        window.removeEventListener('touchmove',   onMove);
        window.removeEventListener('touchend',    onUp);
        window.removeEventListener('touchcancel', onUp);
        self.viewport.removeEventListener('dragstart', onNativeDrag);
        self.viewport.removeEventListener('click',     onClickCapture, true);
      };
    }

    // ── Keyboard ──────────────────────────────────────────────────────

    _handleKeydown(e) {
      if (document.activeElement !== this.element) return;
      if (e.keyCode === 37) {
        this.options.rightToLeft ? this.next() : this.previous();
      } else if (e.keyCode === 39) {
        this.options.rightToLeft ? this.previous() : this.next();
      }
    }

    // ── Events ────────────────────────────────────────────────────────

    on(eventName, listener) {
      if (!eventName || !listener) return this;
      var a = this._listeners[eventName] = this._listeners[eventName] || [];
      if (a.indexOf(listener) === -1) a.push(listener);
      return this;
    }

    off(eventName, listener) {
      var a = this._listeners[eventName];
      if (!a) return this;
      if (listener) { this._listeners[eventName] = a.filter(function(fn) { return fn !== listener; }); }
      else { delete this._listeners[eventName]; }
      return this;
    }

    once(eventName, listener) {
      var self = this;
      function w() { listener.apply(self, arguments); self.off(eventName, w); }
      return this.on(eventName, w);
    }

    allOff() { this._listeners = {}; }

    _emit(eventName, args) {
      var a = this._listeners[eventName];
      if (!a || !a.length) return;
      a.slice().forEach(function(fn) { try { fn.apply(this, args || []); } catch (err) { console.error(err); } }.bind(this));
    }
  }

  // ── Defaults ──────────────────────────────────────────────────────

  ThemeCarousel.defaults = {
    accessibility: true,
    adaptiveHeight: false,
    cellAlign: 'center',
    cellSelector: undefined,
    contain: false,
    draggable: '>1',
    dragThreshold: 3,
    fade: false,
    freeScrollFriction: 0.075,
    friction: 0.28,
    initialIndex: 0,
    pageDots: false,
    percentPosition: true,
    prevNextButtons: false,
    resize: true,
    rightToLeft: false,
    selectedAttraction: 0.025,
    setGallerySize: true,
    wrapAround: false
  };

  // ── iOS touch-scroll prevention for draggable carousels ───────────

  var touchingCarousel = false, touchStartCoords;

  document.body.addEventListener('touchstart', function(e) {
    var sliderEl = e.target.closest('.slide-wrapper');
    if (sliderEl) {
      var carousel = sliderEl.closest('.slide-enabled');
      var inst = carousel ? ThemeCarousel.data(carousel) : null;
      if (inst && inst.isDraggable) {
        touchingCarousel = true;
      } else {
        touchingCarousel = false;
        return;
      }
    } else {
      touchingCarousel = false;
      return;
    }
    touchStartCoords = { x: e.touches[0].pageX, y: e.touches[0].pageY };
  });

  document.body.addEventListener('touchmove', function(e) {
    if (!(touchingCarousel && e.cancelable)) return;
    var mv = {
      x: e.touches[0].pageX - touchStartCoords.x,
      y: e.touches[0].pageY - touchStartCoords.y
    };
    if (Math.abs(mv.x) > 8) e.preventDefault();
  }, { passive: false });

  // ── Expose globally ───────────────────────────────────────────────

  window.ThemeFlickity = ThemeCarousel;
})();
